<!--
  @description Custom single-select dropdown backed by radio inputs. Use instead of a native
  `<select>` when the design requires a styled dropdown list — for example, picking a
  category, language, or difficulty level in admin forms. Only one `Item` can be checked at
  a time; selecting a new item automatically unchecks the previous one.

  The dropdown list closes when the user picks an item or clicks anywhere outside it.
  The `touched` prop must be driven by the parent (e.g. set to `true` on form submit) to
  reveal validation errors; the component does not set it internally.

  @example
  <Select
    id="category-select"
    title="Category"
    :items="categoryItems"
    :touched="formSubmitted"
    :validation="{ required: true }"
    @update:modelValue="(items, status) => { form.category = items; catStatus = status }"
  />

  @accessibility The hidden radio inputs provide keyboard navigability inside the open list.
  The trigger is a readonly text `<input>` — it is focusable and opens on click. Because the
  visible control is not a native `<select>`, add `role="combobox"` and appropriate ARIA
  attributes at the usage site if full ARIA compliance is required.
-->
<template>
  <div @click="handleClickOusideUl" class="select">
    <label v-if="title" class="select__title" :for="id"
      >{{ title }}<span v-if="!validation.required"> ({{ $t('global.optional') }})</span></label
    >
    <div class="select__input-group" :class="classes" ref="inputRef">
      <input
        :id="id"
        :name="title"
        :placeholder="placeholder"
        :value="selectedItem?.label || placeholder || 'Veuillez faire une sélection'"
        type="text"
        readonly
        class="select__trigger"
        @click="toggleList"
      />
      <i
        class="select__chevron fi fi-ts-angle-small-down"
        :class="{
          'select__chevron--inverted': showList,
          'select__chevron--upright': !showList
        }"
        @click="toggleList"
      ></i>
    </div>
    <ul v-show="showList" class="select__list" ref="ulRef">
      <li
        v-for="item in items"
        :key="item.id"
        class="select__list-item"
        @click="updateValue(item.id)"
      >
        <input
          :id="`item-${item.id}`"
          :name="title"
          type="radio"
          :value="item.value"
          :checked="item.checked"
          class="select__radio"
          @change="updateValue(item.id)"
        />
        <label
          :for="`item-${item.id}`"
          class="select__option-label"
          @change="updateValue(item.id)"
          >{{ item.label }}</label
        >
      </li>
    </ul>
    <p v-if="!status.valid && touched" class="select__error">
      {{ status.message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type { Status, ValidationParams, Item } from '@/types/Types'
import { validateOneSelected } from '@/Validation/validation'

import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  /** Unique HTML id applied to the trigger input element. */
  id: {
    type: String,
    required: true
  },
  /** Label text rendered above the dropdown trigger. Omit to hide the label. */
  title: {
    type: String,
    required: false,
    default: undefined
  },
  /**
   * Placeholder text shown in the trigger when no item is selected. Falls back to a
   * hardcoded French string when omitted — provide a translated value via `t()` at the
   * usage site.
   */
  placeholder: {
    type: String,
    required: false,
    default: undefined
  },
  /**
   * Validation rules. Only `required` is evaluated: when true, at least one item must be
   * selected or the status becomes invalid.
   */
  validation: {
    type: Object as () => ValidationParams,
    required: false,
    default: () => ({
      required: false,
      minLength: undefined,
      maxLength: undefined,
      pattern: undefined
    })
  },
  /**
   * Controls whether validation errors are visible. Set to `true` after a form submission
   * attempt. The component never sets this itself — the parent owns it.
   */
  touched: {
    type: Boolean,
    required: true,
    default: false
  },
  /**
   * List of selectable options. Each `Item` carries its own `id`, `label`, `value`, and
   * `checked` state. The component mutates `checked` in-place on selection; only one item
   * can be checked at a time.
   */
  items: {
    type: Array as () => Item[],
    required: true
  }
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: Item[], status: Status): void
}>()

const selectedItem = computed(() => {
  if (props.items === undefined) return undefined
  return props.items.find((item) => item.checked)
})

function updateValue(id: string) {
  if (!id) return
  if (props.items === undefined) return

  const updatedItems = props.items.map((item: Item) =>
    item.id === id ? { ...item, checked: !item.checked } : { ...item, checked: false }
  )

  showList.value = false

  emit('update:modelValue', updatedItems, status.value)
  validateValue(updatedItems)
}

const status = ref({ valid: true, message: '', touched: false } as Status)
function validateValue(value: Item[]) {
  if (props.validation.required) {
    const checked = value.filter((item: Item) => item.checked)
    status.value = validateOneSelected(checked, t)
  } else status.value = { valid: true, message: '', touched: false }
}

const classes = computed(() => ({
  'border-danger': !status.value.valid && status.value.touched,
  'border-success': status.value.valid && status.value.touched,
  'select__input-group--placeholder': selectedItem.value === undefined
}))

let ulRef = ref<HTMLElement | undefined>()
let inputRef = ref<HTMLElement | undefined>()
function handleClickOusideUl(event: MouseEvent) {
  if (
    ulRef.value &&
    !ulRef.value.contains(event.target as Node) &&
    inputRef.value &&
    !inputRef.value.contains(event.target as Node)
  ) {
    showList.value = false
  }
}

let showList = ref(false)
function toggleList() {
  showList.value = !showList.value
}

onMounted(() => {
  document.addEventListener('click', handleClickOusideUl)
})
</script>

<style lang="scss" scoped>
.select {
  display: flex;
  flex-direction: column;
  margin: 1rem 0.5rem;
  position: relative;

  &__title {
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: var(--text);
  }

  &__input-group {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.5rem;
    width: 100%;
    border: 1px solid var(--input-background);
    border-radius: $xl-border-radius;
    background-color: var(--input-background);

    &:focus-within {
      border: 1px solid var(--black);
    }

    &--placeholder {
      color: var(--black-soft);
    }
  }

  &__chevron {
    margin-right: 0.5rem;
    cursor: pointer;
    font-size: 1.4rem;
    transition: all 0.2s ease-in-out;
    color: var(--text);

    &--inverted {
      transform: rotate(180deg) translateY(0.2rem);
    }

    &--upright {
      transform: translateY(0.2rem);
    }
  }

  &__trigger {
    padding: 0.5rem 1rem;
    border-radius: $xl-border-radius;
    border: none;
    font-size: 1rem;
    width: 100%;
    background-color: var(--input-background);
    color: var(--text);
    cursor: pointer;

    &:focus {
      outline: none;
    }
  }

  &__list {
    box-sizing: border-box;
    position: absolute;
    top: 75px;
    z-index: 3;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--input-background);
    border: 1px solid var(--input-background);
    border-radius: $xl-border-radius;
    padding: 0.3rem;
    max-height: 200px;
    overflow-y: auto;
  }

  &__list-item {
    box-sizing: border-box;
    list-style: none;
    padding: 0.5rem $base-spacing;
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: $xl-border-radius;
    cursor: pointer;

    &:hover {
      background-color: var(--tertiary);
    }
  }

  &__radio {
    display: none;
  }

  &__option-label {
    cursor: pointer;
    width: 100%;
    margin-bottom: 0;
    color: var(--text-dark);
  }

  &__error {
    margin-top: 0.2rem;
    font-size: 0.8rem;
    color: var(--danger);
  }
}
</style>
